import jsPDF from 'jspdf';
import {
  BRAND,
  BRAND_DARK,
  BRAND_TINT,
  TEXT_DARK,
  loadUploadedImageAsDataUrl,
  loadLogoAsDataUrl,
  getImageDimensions,
  getImageFormat,
  formatDate,
  formatMoney,
} from './pdfBrand';

const PAGE_MARGIN = 15;
const BLANK_LINE = '_'.repeat(32);

/**
 * Generates a one-page Payment Receipt Voucher PDF for a single ledger
 * payment against a contract, and triggers a browser download.
 * `contract` is the contract record the payment was recorded against;
 * `payment` is one row from contractAPI.listPayments/addPayment.
 */
export const generateReceiptPdf = async (contract, payment) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a5' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  let y = PAGE_MARGIN;

  const [logoDataUrl, signatureDataUrl] = await Promise.all([
    loadLogoAsDataUrl(),
    loadUploadedImageAsDataUrl(contract.provider_signature_url),
  ]);

  // ── Header: logo + title, plain background with a colored rule ────────
  if (logoDataUrl) {
    const dims = await getImageDimensions(logoDataUrl);
    if (dims) {
      const logoHeight = 14;
      const logoWidth = (dims.width / dims.height) * logoHeight;
      doc.addImage(logoDataUrl, getImageFormat(logoDataUrl), PAGE_MARGIN, y, logoWidth, logoHeight);
    }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND_DARK);
  doc.text('PAYMENT RECEIPT VOUCHER', pageWidth - PAGE_MARGIN, y + 6, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(110, 110, 110);
  doc.text(contract.provider_company_name || 'CloudScreen / Cloudnet Softwares', pageWidth - PAGE_MARGIN, y + 11, { align: 'right' });

  y += 18;
  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.8);
  doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y);
  y += 8;

  // ── Receipt No. / Date ──────────────────────────────────────────────
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text('Receipt No.: ', PAGE_MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_DARK);
  doc.text(payment.receipt_no || BLANK_LINE, PAGE_MARGIN + doc.getTextWidth('Receipt No.: '), y);

  const dateLabel = 'Date: ';
  const dateValue = formatDate(payment.payment_date) || '____ / ____ / ______';
  const dateValueWidth = doc.getTextWidth(dateValue);
  doc.text(dateValue, pageWidth - PAGE_MARGIN, y, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text(dateLabel, pageWidth - PAGE_MARGIN - dateValueWidth, y, { align: 'right' });
  y += 9;

  // ── Received From / For Contract ─────────────────────────────────────
  const field = (label, value) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_DARK);
    doc.text(`${label}: `, PAGE_MARGIN, y);
    const labelWidth = doc.getTextWidth(`${label}: `);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_DARK);
    const lines = doc.splitTextToSize(value || BLANK_LINE, contentWidth - labelWidth);
    doc.text(lines, PAGE_MARGIN + labelWidth, y);
    y += Math.max(lines.length, 1) * 5.5;
  };

  field('Received From', contract.client_company_name);
  field('Contact Person', contract.client_contact_person);
  field('For Contract (Agreement No.)', contract.agreement_no);
  field('Payment Method', payment.payment_method);
  y += 2;

  // ── Amount highlight box - the one number on this document that matters most ──
  const amountText = `THB ${formatMoney(payment.amount)}`;
  const boxHeight = 16;
  doc.setFillColor(...BRAND_TINT);
  doc.roundedRect(PAGE_MARGIN, y, contentWidth, boxHeight, 2, 2, 'F');
  doc.setFillColor(...BRAND);
  doc.rect(PAGE_MARGIN, y, 2.5, boxHeight, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_DARK);
  doc.text('AMOUNT RECEIVED', PAGE_MARGIN + 6, y + 6);
  doc.setFontSize(15);
  doc.setTextColor(...TEXT_DARK);
  doc.text(amountText, PAGE_MARGIN + 6, y + 13);
  y += boxHeight + 8;

  if (payment.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND_DARK);
    doc.text('Notes: ', PAGE_MARGIN, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_DARK);
    const lines = doc.splitTextToSize(payment.notes, contentWidth - doc.getTextWidth('Notes: '));
    doc.text(lines, PAGE_MARGIN + doc.getTextWidth('Notes: '), y);
    y += lines.length * 5.5 + 4;
  }

  // ── Running balance summary ───────────────────────────────────────────
  y += 2;
  doc.setDrawColor(220);
  doc.setLineWidth(0.3);
  doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y);
  y += 6;
  field('Contract Total Payable', `THB ${formatMoney(contract.total_payable)}`);
  field('Total Received To Date', `THB ${formatMoney(contract.payment_received)}`);
  field('Balance Remaining', `THB ${formatMoney(contract.balance)}`);
  y += 6;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND_DARK);
  doc.text('Received By: ', PAGE_MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_DARK);
  doc.text(payment.received_by_name || BLANK_LINE, PAGE_MARGIN + doc.getTextWidth('Received By: '), y);

  if (signatureDataUrl) {
    try {
      doc.addImage(signatureDataUrl, getImageFormat(signatureDataUrl), pageWidth - PAGE_MARGIN - 32, y - 10, 30, 12);
    } catch {
      // Corrupt/unsupported image data - leave the signature spot blank rather than failing the whole PDF.
    }
  }
  doc.setDrawColor(180);
  doc.setLineWidth(0.2);
  doc.line(pageWidth - PAGE_MARGIN - 34, y + 3, pageWidth - PAGE_MARGIN, y + 3);
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text('Authorized Signature', pageWidth - PAGE_MARGIN, y + 7, { align: 'right' });

  // ── Footer ───────────────────────────────────────────────────────────
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.5);
  doc.line(PAGE_MARGIN, pageHeight - 14, pageWidth - PAGE_MARGIN, pageHeight - 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text('CloudScreen is a brand of Cloudnet Software Co., Ltd.', PAGE_MARGIN, pageHeight - 9);
  doc.setTextColor(...BRAND_DARK);
  doc.setFontSize(8);
  doc.text(contract.provider_phone || '+66 94 871 2350', pageWidth - PAGE_MARGIN, pageHeight - 9, { align: 'right' });

  doc.save(`Receipt-${payment.receipt_no || 'voucher'}.pdf`);
};
